/**
 * Shared Constants - SINGLE SOURCE OF TRUTH
 * 
 * All constants that need to be shared between frontend and backend
 * should be defined here and imported where needed.
 * 
 * DO NOT duplicate these values elsewhere!
 */

// =============================================================================
// Controller Identity
// =============================================================================

/**
 * AI Controller ID - used for all Firestore queries and data separation
 */
export const CONTROLLER_ID = 'ai-controller-1';

// =============================================================================
// Firestore Collections
// =============================================================================

/**
 * All Firestore collection paths for the AI Controller
 * All prefixed with 'ai_' to separate from production data
 */
export const COLLECTIONS = {
  // Core entities
  controllers: 'ai_controllers',
  devices: 'ai_devices',
  rooms: 'ai_rooms',
  sensors: 'ai_sensors',
  sensorRegistry: 'ai_sensor_registry',
  sensorData: 'ai_sensor_data',
  
  // Automation
  rules: 'ai_rules',
  ruleStates: 'ai_rule_states',
  ruleExecutions: 'ai_rule_executions',
  
  // Batches
  batches: 'ai_batches',
  
  // Alerts & Notifications
  alerts: 'ai_alerts',
  
  // Logging
  logs: 'ai_system_logs',
  
  // Commands (controller <-> UI communication)
  commands: 'ai_commands',
  
  // AI Knowledge
  knowledge: 'ai_knowledge',
  aiSuggestions: 'ai_suggestions',
  
  // Settings
  settings: 'ai_controller_settings',
} as const;

/**
 * Subcollection paths under controllers/{controllerId}/
 * These are NOT prefixed with 'ai_' since they're already scoped to controllers
 */
export const SUBCOLLECTIONS = {
  sensorRegistry: 'sensor_registry',
  devices: 'devices',
  rules: 'rules',
  commands: 'commands',
  summaries10min: '10min_summaries',
} as const;

// =============================================================================
// Log Event Types
// =============================================================================

/**
 * Standardized event types for logging
 * Use these constants instead of string literals
 */
export const LOG_EVENTS = {
  // Rule lifecycle events
  RULE_EVALUATED: 'rule_evaluated',
  RULE_CONDITION_MET: 'rule_condition_met',
  RULE_TRIGGERED: 'rule_triggered',
  RULE_EXECUTED: 'rule_executed',
  RULE_COMPLETED: 'rule_completed',
  RULE_ERROR: 'rule_error',
  RULE_RETRY: 'rule_retry',
  RULE_OSCILLATION: 'rule_oscillation',
  RULE_DISABLED: 'rule_disabled',
  RULE_ENABLED: 'rule_enabled',
  
  // Device control events
  DEVICE_COMMAND: 'device_command',
  DEVICE_VERIFIED: 'device_verified',
  DEVICE_RETRY: 'device_retry',
  DEVICE_FAILED: 'device_failed',
  DEVICE_LOCK_ACQUIRED: 'device_lock_acquired',
  DEVICE_LOCK_RELEASED: 'device_lock_released',
  DEVICE_LOCK_WAIT: 'device_lock_wait',
  
  // WAGO connection events
  WAGO_CONNECTED: 'wago_connected',
  WAGO_DISCONNECTED: 'wago_disconnected',
  WAGO_RECONNECTING: 'wago_reconnecting',
  WAGO_RECONNECTED: 'wago_reconnected',
  WAGO_RECONNECT_FAILED: 'wago_reconnect_failed',
  WAGO_COMMAND_SENT: 'wago_command_sent',
  WAGO_COMMAND_FAILED: 'wago_command_failed',
  
  // Firestore sync events
  FIRESTORE_CONNECTED: 'firestore_connected',
  FIRESTORE_DISCONNECTED: 'firestore_disconnected',
  FIRESTORE_OFFLINE: 'firestore_offline',
  FIRESTORE_ONLINE: 'firestore_online',
  FIRESTORE_QUEUE_ADD: 'firestore_queue_add',
  FIRESTORE_QUEUE_FLUSH: 'firestore_queue_flush',
  
  // System health events
  WATCHDOG_CHECK: 'watchdog_check',
  WATCHDOG_TIMEOUT: 'watchdog_timeout',
  HEALTH_CHECK: 'health_check',
  
  // Mode events
  MODE_CHANGE: 'mode_change',
  
  // Controller lifecycle
  CONTROLLER_START: 'controller_start',
  CONTROLLER_STOP: 'controller_stop',
  CONTROLLER_ERROR: 'controller_error',
  
  // Sensor events
  SENSOR_DATA_RECEIVED: 'sensor_data_received',
  SENSOR_DATA_STORED: 'sensor_data_stored',
  SENSOR_ERROR: 'sensor_error',
  
  // AI events
  AI_DECISION: 'ai_decision',
  AI_OVERRIDE: 'ai_override',
  AI_SUGGESTION: 'ai_suggestion',
  AI_LEARNING: 'ai_learning',
  
  // HVAC events
  HVAC_POWER: 'hvac.setPower',
  HVAC_SETPOINT: 'hvac.setTemperature',
  HVAC_MODE: 'hvac.setMode',
  HVAC_FAN_SPEED: 'hvac.setFanSpeed',
  HVAC_STATE_TRANSITION: 'hvac.stateTransition',
  HVAC_SENSOR_ASSIGNED: 'hvac.setExternalSensor',
  HVAC_DESIRED_TEMP: 'hvac.setDesiredTemp',
  HVAC_PROTECTION_CONFIG: 'hvac.setProtectionConfig',
  HVAC_CONTROL_LOOP: 'hvac.enableControl',
  HVAC_CONNECTED: 'hvac.connected',
  HVAC_DISCONNECTED: 'hvac.disconnected',
  HVAC_ERROR: 'hvac.error',
  
  // Irrigation events
  IRRIGATION_START: 'irrigation.start',
  IRRIGATION_STOP: 'irrigation.stop',
  IRRIGATION_SCHEDULED: 'irrigation.scheduled',
  IRRIGATION_COMPLETED: 'irrigation.completed',
  IRRIGATION_SKIPPED: 'irrigation.skipped',
  IRRIGATION_OVERFLOW: 'irrigation.overflow',
  TABLE_IRRIGATION_START: 'table_irrigation.start',
  TABLE_IRRIGATION_STOP: 'table_irrigation.stop',
  
  // Schedule events
  SCHEDULE_TRIGGERED: 'schedule.triggered',
  SCHEDULE_CREATED: 'schedule.created',
  SCHEDULE_UPDATED: 'schedule.updated',
  SCHEDULE_DELETED: 'schedule.deleted',
  SCHEDULE_ENABLED: 'schedule.enabled',
  SCHEDULE_DISABLED: 'schedule.disabled',
  
  // Hardware events
  HARDWARE_RELAY_ON: 'hardware.relay_on',
  HARDWARE_RELAY_OFF: 'hardware.relay_off',
  HARDWARE_ANALOG_SET: 'hardware.analog_set',
  HARDWARE_CONNECTION_LOST: 'hardware.connection_lost',
  HARDWARE_CONNECTION_RESTORED: 'hardware.connection_restored',
  HARDWARE_ERROR: 'hardware.error',
  
  // Proxy events
  PROXY_STARTED: 'proxy.started',
  PROXY_STOPPED: 'proxy.stopped',
  PROXY_CONNECTED: 'proxy.connected',
  PROXY_DISCONNECTED: 'proxy.disconnected',
  PROXY_ERROR: 'proxy.error',
  
  // SDI-12 sensor events
  SDI12_READING: 'sdi12.reading',
  SDI12_ERROR: 'sdi12.error',
  SDI12_CONNECTED: 'sdi12.connected',
  SDI12_DISCONNECTED: 'sdi12.disconnected',
  
  // SNMP events
  SNMP_READING: 'snmp.reading',
  SNMP_ERROR: 'snmp.error',
  SNMP_CONNECTED: 'snmp.connected',
  SNMP_DISCONNECTED: 'snmp.disconnected',
  
  // Crop steering events
  CROP_STEERING_PHASE_CHANGE: 'crop_steering.phase_change',
  CROP_STEERING_DECISION: 'crop_steering.decision',
} as const;

// =============================================================================
// Log Levels
// =============================================================================

export const LOG_LEVELS = {
  DEBUG: 'debug',
  INFO: 'info',
  WARN: 'warn',
  ERROR: 'error',
} as const;

// =============================================================================
// Rule States
// =============================================================================

export const RULE_STATES = {
  STANDBY: 'standby',
  ACTIVE: 'active',
  ERROR: 'error',
  DISABLED: 'disabled',
  OSCILLATION_DISABLED: 'oscillation_disabled',
} as const;

// =============================================================================
// Controller Modes
// =============================================================================

export const CONTROLLER_MODES = {
  MANUAL: 'manual',
  AI: 'ai',
} as const;

// =============================================================================
// Type exports for TypeScript
// =============================================================================

export type CollectionName = keyof typeof COLLECTIONS;
export type LogEvent = typeof LOG_EVENTS[keyof typeof LOG_EVENTS];
export type LogLevel = typeof LOG_LEVELS[keyof typeof LOG_LEVELS];
export type RuleState = typeof RULE_STATES[keyof typeof RULE_STATES];
export type ControllerMode = typeof CONTROLLER_MODES[keyof typeof CONTROLLER_MODES];
